/**
 * Rank insignia drawn in CSS (05 §10): chevrons and rockers for the enlisted ranks, bars, leaf
 * and eagle for the officers. `insigniaSpec` is the pure description; `rankInsignia` builds it.
 */

import './insignia.css';
import { el } from '../dom';
import { rankAbbr, rankIndex } from '../text';

export interface InsigniaSpec {
  chevrons: number;
  rockers: number;
  star: boolean;
  bars: number;
  leaf: boolean;
  eagle: boolean;
  /** Officers' metal: gold reads amber, silver reads paper. */
  metal: 'gold' | 'silver';
}

const ENLISTED: ReadonlyArray<[chevrons: number, rockers: number, star: boolean]> = [
  [1, 0, false], // PFC
  [2, 0, false], // CPL
  [3, 0, false], // SGT
  [3, 1, false], // SSG
  [3, 2, false], // PSG
  [3, 3, true], // SGM
];

/** Rank index 0-11 (PFC-COL) to what is drawn. Pure. */
export function insigniaSpec(rank: number): InsigniaSpec {
  const i = rankIndex(rank);
  const none = { chevrons: 0, rockers: 0, star: false, bars: 0, leaf: false, eagle: false };
  const enlisted = ENLISTED[i];
  if (enlisted !== undefined) {
    return { ...none, chevrons: enlisted[0], rockers: enlisted[1], star: enlisted[2], metal: 'gold' };
  }
  switch (i) {
    case 6:
      return { ...none, bars: 1, metal: 'gold' };
    case 7:
      return { ...none, bars: 1, metal: 'silver' };
    case 8:
      return { ...none, bars: 2, metal: 'silver' };
    case 9:
      return { ...none, leaf: true, metal: 'gold' };
    case 10:
      return { ...none, leaf: true, metal: 'silver' };
    default:
      return { ...none, eagle: true, metal: 'silver' };
  }
}

export function rankInsignia(rank: number): HTMLElement {
  const spec = insigniaSpec(rank);
  const root = el('div', `menu-insignia menu-insignia-${spec.metal}`);
  root.setAttribute('role', 'img');
  root.setAttribute('aria-label', `RANK ${rankAbbr(rank)}`);
  for (let i = 0; i < spec.chevrons; i++) root.append(el('span', 'menu-chevron'));
  if (spec.star) root.append(el('span', 'menu-rank-star'));
  for (let i = 0; i < spec.rockers; i++) root.append(el('span', 'menu-rocker'));
  if (spec.bars > 0) {
    const bars = el('span', 'menu-rank-bars');
    for (let i = 0; i < spec.bars; i++) bars.append(el('span', 'menu-rank-bar'));
    root.append(bars);
  }
  if (spec.leaf) root.append(el('span', 'menu-rank-leaf'));
  if (spec.eagle) root.append(el('span', 'menu-rank-eagle'));
  return root;
}
